import axios from 'axios';

const UPLOAD_TIMEOUT_MS = 30000;

type S3UploadErrorCode = 'ERR_NETWORK' | 'ETIMEDOUT' | 'ERR_BAD_RESPONSE';

/** A failed S3 PUT. Never carries the signed URL. */
export class S3UploadError extends Error {
  constructor(readonly code: S3UploadErrorCode, readonly status?: number) {
    super(status ? `S3 upload failed with HTTP ${status}` : `S3 upload failed (${code})`);
    this.name = 'S3UploadError';
  }
}

/**
 * Maps Axios failures to errors without signed URLs or request configuration.
 * Preserves non-Axios failures and keeps unclassified Axios failures non-retryable.
 */
const toUploadError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return error;
  }
  // Axios 0.27 attaches the failed XHR as a status 0 response for network errors.
  if (error.response?.status) {
    return new S3UploadError('ERR_BAD_RESPONSE', error.response.status);
  }
  if (error.code === 'ETIMEDOUT') {
    return new S3UploadError('ETIMEDOUT');
  }
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
    return new S3UploadError('ERR_NETWORK');
  }
  return new Error(`S3 upload request failed (${error.code ?? 'unknown'})`);
};

/**
 * Performs one Axios PUT with a 30-second timeout and no cross-origin credentials.
 * Does not retry; useS3Upload owns the retry policy.
 *
 * @param url Presigned S3 URL, validated before making the request.
 * @param file File bytes to upload, using the file's Content-Type when available.
 * @returns Resolves to `{ status }`, the HTTP status S3 returned for the successful PUT.
 * @throws {S3UploadError} For recognized HTTP, network, or timeout failures.
 * @throws {Error} For invalid URLs or other request/setup failures.
 */
export const uploadFileToS3 = async (url: string, file: File) => {
  // An unparsable URL is a setup error: fail before the network so it is never retried.
  new URL(url);
  try {
    const response = await axios.put(url, file, {
      headers: file.type ? { 'Content-Type': file.type } : undefined,
      timeout: UPLOAD_TIMEOUT_MS,
      transitional: { clarifyTimeoutError: true },
      withCredentials: false,
    });
    return { status: response.status };
  } catch (error) {
    throw toUploadError(error);
  }
};

export type S3Uploader = typeof uploadFileToS3;
